import React from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { useDashboardSplit } from '../hooks/useDashboardSplit';
import { DASH_TOOLTIP } from './tooltipStyle';

/**
 * Shared horizontal bar for "Vendor Split over X" widgets. One hue per card
 * (barColor, a var(--dash-chart-N)); value label at each bar end, optional "· NN%".
 */
const BarSplitChart = ({
  title,
  endpoint,
  params,
  cornerLabel,
  barColor = 'var(--dash-chart-1)',
  showPercent = false,
  transform,
  maxRows,
  areaClassName = 'h-[210px]',
  valueLabel = 'Vendors',
  // Opt-in: render a small control in the header that lets the user flip the
  // bars between highest-first (default) and lowest-first.
  sortToggle = false,
  // Opt-in: fold entries whose value is below this threshold into a single
  // trailing "Others" bar. The user reveals the full tail by clicking that bar
  // (or the header toggle). Set to a positive number to enable.
  collapseBelow,
  othersLabel = 'Others',
}) => {
  const { data, loading } = useDashboardSplit(endpoint, params);
  const collapsible = typeof collapseBelow === 'number' && collapseBelow > 0;
  const [sortDir, setSortDir] = React.useState('desc');
  const [expanded, setExpanded] = React.useState(false);

  const rows = React.useMemo(() => {
    const base = Array.isArray(data) ? data : [];
    const shaped = transform ? transform(base) : base;
    // Always rank by value descending first so the top-N selection is stable
    // regardless of the display direction the user picks…
    const ranked = [...shaped].sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));

    // Collapsible "Others": until expanded, entries below `collapseBelow` are
    // folded into one trailing bucket the user can click to reveal. Pinned last
    // so recharts (which draws data[0] at the top) renders it at the bottom.
    if (collapsible && !expanded) {
      const big = ranked.filter((r) => (Number(r.value) || 0) >= collapseBelow);
      const smallSum = ranked
        .filter((r) => (Number(r.value) || 0) < collapseBelow)
        .reduce((sum, r) => sum + (Number(r.value) || 0), 0);
      return smallSum > 0 ? [...big, { name: othersLabel, value: smallSum, __others: true }] : big;
    }

    const top = (typeof maxRows === 'number' && maxRows > 0) ? ranked.slice(0, maxRows) : ranked;
    // …then flip for display when the user asks for lowest-first. Recharts puts
    // data[0] at the top, so a descending array reads highest→lowest top-to-bottom.
    return sortDir === 'asc' ? [...top].reverse() : top;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, transform, maxRows, sortDir, collapsible, collapseBelow, expanded, othersLabel]);

  // How many entries are hidden inside the collapsed "Others" bucket.
  const hiddenCount = React.useMemo(() => {
    if (!collapsible) return 0;
    const base = Array.isArray(data) ? data : [];
    const shaped = transform ? transform(base) : base;
    return shaped.filter((r) => (Number(r.value) || 0) < collapseBelow).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, transform, collapsible, collapseBelow]);

  // Clicking the "Others" bar reveals the full tail.
  const handleBarClick = (entry) => {
    if (!collapsible) return;
    const isOthers = entry?.__others ?? entry?.payload?.__others;
    if (isOthers) setExpanded(true);
  };

  const total = rows.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
  const isEmpty = !loading && rows.length === 0;

  // This card is reused at very different widths — full-width on the dashboard,
  // but barely 200px when three charts share one half-width card on the MIS
  // report. With a fixed 82px axis and a 70px right margin there was nothing left
  // to draw bars in, so every bar but the largest collapsed and the value labels
  // spilled over each other. Measure the real width and adapt to it.
  const areaRef = React.useRef(null);
  const [areaWidth, setAreaWidth] = React.useState(0);
  React.useEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setAreaWidth(entry.contentRect.width));
    ro.observe(el);
    setAreaWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const narrow = areaWidth > 0 && areaWidth < 300;

  // Category labels are often 2-letter country codes; 82px of axis for "IN" is
  // pure waste. Size the axis to the longest label actually present.
  const longest = rows.reduce((m, r) => Math.max(m, String(r.name ?? '').length), 0);
  const axisWidth = Math.round(Math.min(narrow ? 56 : 90, Math.max(28, Math.min(longest, 16) * 6.4 + 10)));
  // Truncate ticks to what the axis can actually hold — recharts wraps a tick
  // wider than the axis onto a second line, which collides with its neighbours
  // ("Freight OnBoard" at 15 chars overflowed the 16-char allowance).
  const maxTickChars = Math.max(4, Math.floor((axisWidth - 8) / 6));

  // Abbreviate so a five-digit count cannot outgrow the space reserved for it.
  const abbrev = (n) =>
    n >= 10000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k` : n.toLocaleString();

  // When space is tight the percentage is dropped from the bar label — it stays
  // in the tooltip, so nothing is lost and the labels stop colliding.
  const showPercentLabel = showPercent && total > 0 && !narrow;
  const labelText = (v) => {
    const n = Number(v) || 0;
    if (!showPercentLabel) return abbrev(n);
    const pct = Math.round((n / total) * 100);
    return `${abbrev(n)} · ${n > 0 && pct < 1 ? '<1' : pct}%`;
  };

  // LabelList's default renderer wraps the label to the BAR's width, so a
  // sliver bar stacks "121 · 2%" into three overlapping lines. Draw a plain
  // <text> at the bar end instead — SVG text never wraps.
  const BarEndLabel = ({ x, y, width, height, value }) => (
    <text
      x={(Number(x) || 0) + (Number(width) || 0) + 5}
      y={(Number(y) || 0) + (Number(height) || 0) / 2}
      dominantBaseline="central"
      style={{ fontSize: 11, fill: 'var(--dash-foreground)' }}
    >
      {labelText(value)}
    </text>
  );

  const rightMargin = showPercentLabel ? 70 : narrow ? 38 : 46;

  const tooltipFormatter = (value) => {
    const n = Number(value) || 0;
    const pct = total > 0 ? ((n / total) * 100).toFixed(1) : '0.0';
    return [`${n.toLocaleString()} · ${pct}%`, valueLabel];
  };

  return (
    <div className="flex flex-col h-full min-h-[200px]">
      <div className="flex items-center justify-between gap-2 mb-2 flex-shrink-0">
        <h3 className="text-sm font-semibold text-dash-foreground">{title}</h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {sortToggle ? (
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
              title="Toggle sort order"
              aria-label={`Sorted ${sortDir === 'desc' ? 'highest first' : 'lowest first'}. Click to toggle.`}
              className="inline-flex items-center gap-1 rounded-md border border-dash-border px-1.5 py-0.5 text-[11px] text-dash-muted-foreground hover:text-dash-foreground hover:bg-dash-muted transition-colors"
            >
              <span aria-hidden="true">{sortDir === 'desc' ? '↓' : '↑'}</span>
              {sortDir === 'desc' ? 'High → Low' : 'Low → High'}
            </button>
          ) : null}
          {collapsible && hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              title={expanded ? `Collapse small entries into "${othersLabel}"` : 'Show all entries individually'}
              aria-expanded={expanded}
              className="inline-flex items-center gap-1 rounded-md border border-dash-border px-1.5 py-0.5 text-[11px] text-dash-muted-foreground hover:text-dash-foreground hover:bg-dash-muted transition-colors"
            >
              <span aria-hidden="true">{expanded ? '▴' : '▾'}</span>
              {expanded ? 'Show less' : `Show all (+${hiddenCount})`}
            </button>
          ) : null}
          {cornerLabel ? <span className="text-[11px] text-dash-muted-foreground">{cornerLabel}</span> : null}
        </div>
      </div>
      <div ref={areaRef} className={`relative ${areaClassName}`} style={{ height: Math.max(150, rows.length * 30) }}>
        {isEmpty ? (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-dash-muted-foreground">
            No data available
          </div>
        ) : (
          // Height is a number, not "100%": the container's height depends on the
          // row count, and ResponsiveContainer can capture the pre-data height
          // and never re-measure — 12 rows crushed into 96px of SVG.
          <ResponsiveContainer width="100%" height={Math.max(150, rows.length * 30)}>
            <BarChart layout="vertical" data={rows} margin={{ top: 2, right: rightMargin, bottom: 2, left: 2 }}>
              <CartesianGrid horizontal={false} stroke="var(--dash-border)" />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={axisWidth}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'var(--dash-muted-foreground)' }}
                tickFormatter={(name) => {
                  if (typeof name !== 'string') return name;
                  const cut = name.length > maxTickChars ? `${name.slice(0, maxTickChars)}…` : name;
                  // Non-breaking spaces: recharts wraps ticks at spaces even when
                  // the text fits, stacking a stray second line into the next row.
                  return cut.replace(/ /g, ' ');
                }}
                interval={0}
              />
              <Tooltip
                cursor={{ fill: 'var(--dash-muted)' }}
                formatter={tooltipFormatter}
                contentStyle={DASH_TOOLTIP.contentStyle}
                labelStyle={DASH_TOOLTIP.labelStyle}
                itemStyle={DASH_TOOLTIP.itemStyle}
              />
              {/* Animation off: labels only mount after the grow animation ends,
                  which made them pop in late (and never render at all in
                  non-compositing environments). */}
              <Bar
                dataKey="value"
                fill={barColor}
                radius={[0, 2, 2, 0]}
                barSize={12}
                isAnimationActive={false}
                onClick={collapsible ? handleBarClick : undefined}
                cursor={collapsible && !expanded ? 'pointer' : undefined}
              >
                {collapsible
                  ? rows.map((entry, i) => (
                      <Cell key={`cell-${i}`} fill={entry.__others ? 'var(--dash-muted-foreground)' : barColor} />
                    ))
                  : null}
                <LabelList dataKey="value" content={BarEndLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BarSplitChart;
